// Mirrors the mobile app's NotificationItem widget so admin and app describe
// device logs the same way.

export const DeviceLogAction = {
  PARCEL: 'parcel',
  WEIGHT: 'weight',
  LOCK_STATUS: 'lock_status',
  PRIMARY_BATTERY_LEVEL: 'primary_battery_level',
  SECONDARY_BATTERY_LEVEL: 'secondary_battery_level',
  PRIMARY_LOW_BATTERY: 'primary_low_battery',
  SECONDARY_LOW_BATTERY: 'secondary_low_battery',
  TARE_CALIBRATION: 'tare_calibration',
  CONNECTED_WIFI_NAME: 'connected_wifi_name',
  INVALID_DELIVERY_CODE: 'invalid_delivery_code',
  PARCEL_DELIVERY_ABORTED: 'parcel_delivery_aborted',
  KEYPAD_BLOCKED: 'keypad_blocked'
};

export const DeviceOpenedClosedThrough = {
  APP: 'APP',
  COURIER: 'COURIER',
  MANUALLY: 'MANUALLY',
  MASTER_CODE: 'MASTER_CODE',
  AUTO: 'AUTO'
};

const DEFAULT_DEVICE_NAME = 'your Post Hubb device';

const BATTERY_ACTIONS = [
  DeviceLogAction.PRIMARY_BATTERY_LEVEL,
  DeviceLogAction.SECONDARY_BATTERY_LEVEL,
  DeviceLogAction.PRIMARY_LOW_BATTERY,
  DeviceLogAction.SECONDARY_LOW_BATTERY
];

// Same as Dart's int.tryParse(value ?? '0') ?? 0
const toInt = (value) => {
  const parsed = parseInt(value ?? '0', 10);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const normalizeAction = (log) => String(log?.action || '').toLowerCase();

const normalizeThrough = (log) => String(log?.device_opened_closed_through || '').toUpperCase();

export const isUnlocked = (log) => String(log?.current_data || '').toUpperCase() === 'UNLOCKED';

// Weight is reported in grams
export const formatWeight = (grams) => {
  if (grams < 1000) return `${grams} g`;
  return `${parseFloat((grams / 1000).toFixed(2))} kg`;
};

const getUserName = (log) => log?.userDetails?.name || 'Unknown';

export const getNotificationIcon = (log) => {
  const action = normalizeAction(log);

  switch (action) {
  case DeviceLogAction.PARCEL:
    return toInt(log?.current_data) > toInt(log?.previous_data) ? 'ri-inbox-archive-line' : 'ri-inbox-unarchive-line';
  case DeviceLogAction.WEIGHT:
    return 'ri-scales-3-line';
  case DeviceLogAction.LOCK_STATUS:
    return isUnlocked(log) ? 'ri-lock-unlock-line' : 'ri-lock-line';
  case DeviceLogAction.TARE_CALIBRATION:
    return 'ri-equalizer-line';
  case DeviceLogAction.CONNECTED_WIFI_NAME:
    return 'ri-wifi-line';
  case DeviceLogAction.INVALID_DELIVERY_CODE:
  case DeviceLogAction.KEYPAD_BLOCKED:
    return 'ri-keyboard-box-line';
  case DeviceLogAction.PARCEL_DELIVERY_ABORTED:
    return 'ri-close-circle-line';
  default:
    break;
  }

  if (BATTERY_ACTIONS.includes(action)) {
    const percent = toInt(log?.current_data);
    if (percent <= 5) return 'ri-battery-line';
    if (percent <= 30) return 'ri-battery-low-line';
    if (percent <= 60) return 'ri-battery-2-line';
    if (percent <= 90) return 'ri-battery-2-fill';
    return 'ri-battery-fill';
  }

  return 'ri-information-line';
};

export const getNotificationTitle = (log) => {
  switch (normalizeAction(log)) {
  case DeviceLogAction.PARCEL:
    return toInt(log?.current_data) > toInt(log?.previous_data) ? 'Parcel Received' : 'Parcel Collected';
  case DeviceLogAction.WEIGHT:
    return 'Weight Changed';
  case DeviceLogAction.LOCK_STATUS:
    return isUnlocked(log) ? 'Access Granted' : 'Door Locked';
  case DeviceLogAction.PRIMARY_BATTERY_LEVEL:
    return 'Primary Battery Level';
  case DeviceLogAction.SECONDARY_BATTERY_LEVEL:
    return 'Backup Battery Level';
  case DeviceLogAction.PRIMARY_LOW_BATTERY:
    return 'Primary Low Battery';
  case DeviceLogAction.SECONDARY_LOW_BATTERY:
    return 'Backup Low Battery';
  case DeviceLogAction.TARE_CALIBRATION:
    return 'Calibration';
  case DeviceLogAction.CONNECTED_WIFI_NAME:
    return 'Connected Wi-Fi';
  case DeviceLogAction.INVALID_DELIVERY_CODE:
    return 'Invalid Access Attempt';
  case DeviceLogAction.PARCEL_DELIVERY_ABORTED:
    return 'Parcel Delivery Aborted';
  case DeviceLogAction.KEYPAD_BLOCKED:
    return 'Device Blocked';
  default:
    return 'Notification';
  }
};

const getLockStatusDescription = (log) => {
  const locked = !isUnlocked(log);
  let actionOwner;
  let actionDesc;

  switch (normalizeThrough(log)) {
  case DeviceOpenedClosedThrough.APP:
    actionOwner = getUserName(log);
    actionDesc = locked ? 'Locked the device from app' : 'Unlocked the device from app';
    break;
  case DeviceOpenedClosedThrough.COURIER:
    actionOwner = log?.courierDetails?.name || 'Unknown';
    actionDesc = locked ? 'Locked the device' : 'Unlocked the device';
    break;
  case DeviceOpenedClosedThrough.MANUALLY:
    actionOwner = 'System';
    actionDesc = locked ? 'Device Locked manually' : 'Device Unlocked manually';
    break;
  case DeviceOpenedClosedThrough.MASTER_CODE:
    actionOwner = 'System';
    actionDesc = locked ? 'Device Locked using Master Code' : 'Device Unlocked using Master Code';
    break;
  case DeviceOpenedClosedThrough.AUTO:
    actionOwner = 'System';
    actionDesc = locked ? 'Device Locked automatically' : 'Device Unlocked automatically';
    break;
  default:
    actionOwner = 'Unknown';
    actionDesc = locked ? 'Locked the device' : 'Unlocked the device';
  }

  return `${actionOwner} - ${actionDesc}`;
};

const getWeightDescription = (log) => {
  const weight = toInt(log?.current_data);
  const base = 'The weight of the device has changed.';
  if (weight < 50) return `${base} Current weight: ${weight} g.`;
  if (weight < 200) return `${base} Current weight: ${weight} g. It might be a small parcel or a letter.`;
  if (weight < 1000) return `${base} Current weight: ${weight} g. It might be a medium-sized parcel.`;
  return `${base} Current weight: ${parseFloat((weight / 1000).toFixed(2))}kg. It might be a large parcel.`;
};

const getParcelDescription = (log) => {
  const currentWeight = toInt(log?.current_data);
  const previousWeight = toInt(log?.previous_data);
  const deviceName = log?.device_alias_name || DEFAULT_DEVICE_NAME;
  const absDiff = Math.abs(currentWeight - previousWeight);

  if (currentWeight > previousWeight) {
    return `A parcel has been delivered to ${deviceName}. Added weight: ${formatWeight(absDiff)}.`;
  }
  if (currentWeight < previousWeight) {
    return `A parcel has been collected from ${deviceName}. Removed weight: ${formatWeight(absDiff)}.`;
  }
  return `Parcel activity detected in ${deviceName}.`;
};

export const getNotificationDescription = (log) => {
  const action = normalizeAction(log);
  const percent = toInt(log?.current_data);

  switch (action) {
  case DeviceLogAction.PARCEL:
    return getParcelDescription(log);
  case DeviceLogAction.WEIGHT:
    return getWeightDescription(log);
  case DeviceLogAction.LOCK_STATUS:
    return getLockStatusDescription(log);
  case DeviceLogAction.PRIMARY_BATTERY_LEVEL:
  case DeviceLogAction.SECONDARY_BATTERY_LEVEL:
    return `The ${action === DeviceLogAction.PRIMARY_BATTERY_LEVEL ? 'Primary' : 'Backup'} battery level has changed. Current level: ${percent}%.`;
  case DeviceLogAction.PRIMARY_LOW_BATTERY:
  case DeviceLogAction.SECONDARY_LOW_BATTERY:
    return `The ${action === DeviceLogAction.PRIMARY_LOW_BATTERY ? 'Primary' : 'Backup'} battery is low. Current level: ${percent}%. Please recharge or replace it soon.`;
  case DeviceLogAction.TARE_CALIBRATION:
    return `${getUserName(log)} - Calibrated the device.`;
  case DeviceLogAction.CONNECTED_WIFI_NAME:
    return `The device connected to Wi-Fi network: ${log?.current_data || 'Unknown'}.`;
  case DeviceLogAction.INVALID_DELIVERY_CODE:
    return 'An attempt was made to open the device using an invalid delivery code. Access was denied for security reasons.';
  case DeviceLogAction.PARCEL_DELIVERY_ABORTED:
    return `Delivery attempt aborted at ${log?.device_alias_name || DEFAULT_DEVICE_NAME}. Parcel was removed before the lid was closed.`;
  case DeviceLogAction.KEYPAD_BLOCKED:
    return 'The device has been temporarily blocked due to too many incorrect code attempts. If this was not you, please review your device security.';
  default:
    return 'Received a notification';
  }
};
